/* eslint-disable @nx/enforce-module-boundaries -- cascadia-react imports predate FP-1161 */
import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  GhostIconButton,
  Illustration,
  Link,
  Loader,
  Modal,
  PrimaryButton,
  Radio,
  RadioGroup,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

type NfcFlowStep = "NFC_QUESTION" | "APP_DOWNLOAD" | "SESSION_QR";

const MYCERTN_IOS_URL = "https://apps.apple.com/us/app/mycertn/id6449458498";
const MYCERTN_ANDROID_URL =
  "https://play.google.com/store/apps/details?id=co.certn.wallet";
// Smart link: wallet.certn.co detects the scanning phone's OS and redirects to
// the Apple App Store or Google Play accordingly. See apps/wallet-web/src/App.tsx.
const MYCERTN_DOWNLOAD_URL = "https://wallet.certn.co";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia("(max-width: 640px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return isMobile;
};

interface NfcIdVerificationFlowProps {
  isOpen: boolean;
  onClose: () => void;
  onFallbackToIframe: () => void;
  trustmaticUrl: string;
  isTrustmaticUrlLoading: boolean;
  trustmaticUrlError: boolean;
  onStartNfcSession: () => Promise<boolean>;
  onNfcSessionComplete: () => void;
}

export function NfcIdVerificationFlow({
  isOpen,
  onClose,
  onFallbackToIframe,
  trustmaticUrl,
  isTrustmaticUrlLoading,
  trustmaticUrlError,
  onStartNfcSession,
  onNfcSessionComplete,
}: NfcIdVerificationFlowProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [step, setStep] = useState<NfcFlowStep>("NFC_QUESTION");
  const [nfcAnswer, setNfcAnswer] = useState<string | null>(null);
  const [isStartingSession, setIsStartingSession] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setStep("NFC_QUESTION");
      setNfcAnswer(null);
      setIsStartingSession(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleNfcContinue = () => {
    if (nfcAnswer === "yes") {
      setStep("APP_DOWNLOAD");
    } else if (nfcAnswer === "no") {
      onFallbackToIframe();
    }
  };

  const handleContinueInBrowser = () => {
    onFallbackToIframe();
  };

  const handleDownloadApp = () => {
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    window.open(isIos ? MYCERTN_IOS_URL : MYCERTN_ANDROID_URL, "_blank");
  };

  const handleDownloadContinue = async () => {
    setIsStartingSession(true);
    const success = await onStartNfcSession();
    setIsStartingSession(false);
    if (success) {
      setStep("SESSION_QR");
    }
  };

  const handleOpenOnPhone = () => {
    window.open(trustmaticUrl, "_blank");
  };

  if (step === "NFC_QUESTION") {
    return (
      <Modal isOpen isDismissable>
        <Dialog width={600}>
          <DialogHeader onClose={onClose} testID="nfc-question-modal-header">
            {t("NFC Identity Verification")}
          </DialogHeader>
          <DialogContent>
            <div className={styles.nfcQuestionContent}>
              <div className={styles.illustrationContainer}>
                <Illustration
                  name="nfc-document"
                  size={160}
                  aria-hidden="true"
                />
              </div>
              <Text style="global/modal-header">
                <Trans>Do you have an NFC-enabled identity document?</Trans>
              </Text>
              <Text style="global/body-primary" color="text-weak">
                <Trans>
                  ePassports have a small, gold-coloured symbol on the front
                  cover of the passport.
                </Trans>
              </Text>
              <RadioGroup
                orientation="horizontal"
                isCard
                stretchChildren
                value={nfcAnswer ?? undefined}
                onChange={setNfcAnswer}
                testID="nfc-question-radio-group"
              >
                <Radio value="yes" testID="nfc-question-yes-radio">
                  {t("Yes, I do")}
                </Radio>
                <Radio value="no" testID="nfc-question-no-radio">
                  {t("No, I don't")}
                </Radio>
              </RadioGroup>
            </div>
          </DialogContent>
          <DialogFooter>
            <PrimaryButton
              testID="nfc-question-continue-button"
              onPress={handleNfcContinue}
              isDisabled={!nfcAnswer}
              variant="brand-primary"
            >
              <Trans>Continue</Trans>
            </PrimaryButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    );
  }

  if (step === "APP_DOWNLOAD") {
    return (
      <Modal isOpen isDismissable>
        <Dialog width={600}>
          <DialogHeader onClose={onClose} testID="nfc-download-modal-header">
            <GhostIconButton
              size="small"
              icon="arrow_back"
              onPress={() => setStep("NFC_QUESTION")}
              aria-label={t("Back")}
              testID="nfc-download-back-button"
            />
          </DialogHeader>
          <DialogContent>
            <div className={styles.downloadContent}>
              <div className={styles.stepSection}>
                <Text style="global/modal-header">
                  {t("Step 1: Get the MyCertn app")}
                </Text>
                {isMobile ? (
                  <>
                    <Text style="global/body-primary">
                      <Trans>
                        Tap the button below to download the MyCertn app.
                      </Trans>
                    </Text>
                    <BasicButton
                      testID="nfc-download-app-button"
                      onPress={handleDownloadApp}
                      rightIcon="open_in_new"
                    >
                      <Trans>Download the app</Trans>
                    </BasicButton>
                  </>
                ) : (
                  <>
                    <Text style="global/body-primary">
                      <Trans>
                        Scan this code with your phone's camera — it's a quick
                        link to download the app.
                      </Trans>
                    </Text>
                    <div className={styles.sessionQrCodeContainer}>
                      <QRCodeCanvas
                        value={MYCERTN_DOWNLOAD_URL}
                        size={200}
                        fgColor="#1B5E20"
                        data-testid="nfc-download-qr-code"
                      />
                    </div>
                  </>
                )}
                <div className={styles.centeredText}>
                  <Text style="global/body-secondary" color="text-weak">
                    <Trans>Available on the App Store and Google Play</Trans>
                  </Text>
                </div>
                <Text style="global/body-primary" color="text-weak">
                  <Trans>
                    Reading your document's chip needs direct access to your
                    phone's hardware, which your browser can't do — that's why
                    this step needs the app.
                  </Trans>
                </Text>
              </div>
              <div className={styles.stepSection}>
                <Text style="global/modal-header">
                  {t("Step 2: Link your app to this background check")}
                </Text>
                <Text style="global/body-primary" color="text-weak">
                  {isMobile ? (
                    <Trans>
                      On the next screen, open this background check in the
                      MyCertn app.
                    </Trans>
                  ) : (
                    <Trans>
                      You will be shown another QR code, which will link your
                      app to this background check session.
                    </Trans>
                  )}
                </Text>
              </div>
            </div>
          </DialogContent>
          <DialogFooter
            leftContent={
              <Link
                testID="nfc-continue-in-browser-link"
                linkStyle="monochrome"
                textStyle="global/body-secondary"
                onPress={handleContinueInBrowser}
              >
                <Trans>Unable to download app?</Trans>
              </Link>
            }
          >
            <PrimaryButton
              testID="nfc-download-continue-button"
              onPress={handleDownloadContinue}
              isLoading={isStartingSession}
              variant="brand-primary"
            >
              <Trans>I downloaded the app</Trans>
            </PrimaryButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    );
  }

  return (
    <Modal isOpen isDismissable>
      <Dialog width={600}>
        <DialogHeader onClose={onClose} testID="nfc-session-modal-header" />
        <DialogContent>
          <div className={styles.sessionContent}>
            {isTrustmaticUrlLoading ? (
              <div
                className={styles.loaderContainer}
                data-testid="nfc-session-loader"
              >
                <Loader type="spinner" />
                <Text style="global/body-primary" color="text-weak">
                  <Trans>Setting up your verification session…</Trans>
                </Text>
              </div>
            ) : trustmaticUrlError || !trustmaticUrl ? (
              <div
                className={styles.errorContainer}
                data-testid="nfc-session-error"
              >
                <Text style="global/modal-header">
                  <Trans>Something went wrong</Trans>
                </Text>
                <Text style="global/body-primary" color="text-weak">
                  <Trans>
                    We couldn't set up your verification session. Please close
                    this window and try again.
                  </Trans>
                </Text>
              </div>
            ) : (
              <>
                <Text style="global/modal-header">
                  {isMobile ? (
                    <Trans>Continue in the MyCertn app</Trans>
                  ) : (
                    <Trans>Scan QR code with MyCertn app</Trans>
                  )}
                </Text>
                {isMobile ? (
                  <>
                    <Text style="global/body-primary">
                      <Trans>
                        Start your identity verification by using the MyCertn
                        app.
                      </Trans>
                    </Text>
                    <BasicButton
                      testID="nfc-session-open-on-phone-button"
                      onPress={handleOpenOnPhone}
                      rightIcon="open_in_new"
                    >
                      <Trans>Open MyCertn app</Trans>
                    </BasicButton>
                  </>
                ) : (
                  <>
                    <Text style="global/body-primary">
                      <Trans>
                        Start your identity verification by using the MyCertn
                        app to scan the QR code below.
                      </Trans>
                    </Text>
                    <div className={styles.sessionQrCodeContainer}>
                      <QRCodeCanvas
                        value={trustmaticUrl}
                        size={200}
                        data-testid="nfc-session-qr-code"
                      />
                    </div>
                  </>
                )}
              </>
            )}
            <div className={styles.centeredText}>
              <Text
                style="global/body-secondary"
                color="text-weak"
                testID="nfc-session-done-hint"
              >
                <Trans>
                  Click "Done" once you've verified your identity in the MyCertn
                  app.
                </Trans>
              </Text>
            </div>
          </div>
        </DialogContent>
        <DialogFooter>
          <PrimaryButton
            testID="nfc-session-done-button"
            onPress={onNfcSessionComplete}
            isDisabled={
              isTrustmaticUrlLoading || trustmaticUrlError || !trustmaticUrl
            }
            variant="brand-primary"
          >
            <Trans>Done</Trans>
          </PrimaryButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

const styles = {
  nfcQuestionContent: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  illustrationContainer: css({
    display: "flex",
    justifyContent: "center",
  }),
  downloadContent: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-6)",
  }),
  stepSection: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  sessionContent: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  qrCodeContainer: css({
    display: "flex",
    justifyContent: "center",
    padding: "var(--space-4) 0",
  }),
  sessionQrCodeContainer: css({
    display: "flex",
    justifyContent: "center",
    padding: "var(--space-5)",
    backgroundColor: "var(--background-accent-neutral-weak)",
    borderRadius: "var(--border-radius-3)",
  }),
  loaderContainer: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "var(--space-4)",
    padding: "var(--space-6) 0",
  }),
  errorContainer: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  centeredText: css({
    textAlign: "center",
  }),
};
